import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowUp, FiHeart, FiSearch, FiShoppingCart } from 'react-icons/fi';
import { useAppSelector } from '../../../store/hooks';
import type { RootState } from '../../../store';
import SearchOverlay from '../../search/SearchOverlay';

const FiSearchIcon = FiSearch as any;
const FiShoppingCartIcon = FiShoppingCart as any;
const FiHeartIcon = FiHeart as any;
const FiArrowUpIcon = FiArrowUp as any;

interface SideFloatingActionBarProps {
  right?: string;
  bottom?: string;
  zIndex?: number;
}

const SideFloatingActionBar: React.FC<SideFloatingActionBarProps> = ({
  right = '32px',
  bottom = '120px',
  zIndex = 1200,
}) => {
  const wishlistItems = useAppSelector((state: RootState) => state.wishlist.items);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const handleScrollToTop = () => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({
      top: 0,
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  };

  return (
    <aside
      aria-label="Quick actions"
      className="fixed bottom-24 right-3 overflow-visible rounded-xl border border-black/10 bg-white shadow-[0_10px_25px_rgba(0,0,0,0.22)] sm:bottom-[120px] sm:right-6 sm:rounded-2xl lg:right-8"
      style={{ zIndex }}
    >
      <div className="flex flex-col divide-y divide-black/10">
        <button
          type="button"
          aria-label="Search"
          title="Search"
          onClick={() => setIsSearchOpen(true)}
          className="flex h-10 w-10 items-center justify-center bg-[#0099ff] text-white transition hover:brightness-110 sm:h-12 sm:w-12 lg:h-14 lg:w-14"
        >
          <FiSearchIcon size={21} />
        </button>

        <Link
          to="/cart"
          aria-label="Cart"
          title="Cart"
          className="flex h-10 w-10 items-center justify-center text-slate-900 transition hover:bg-slate-50 sm:h-12 sm:w-12 lg:h-14 lg:w-14"
        >
          <FiShoppingCartIcon size={21} />
        </Link>

        <Link
          to="/wishlist"
          aria-label="Wishlist"
          title="Wishlist"
          className="relative flex h-10 w-10 items-center justify-center text-slate-900 transition hover:bg-slate-50 sm:h-12 sm:w-12 lg:h-14 lg:w-14"
        >
          <FiHeartIcon size={21} />
          {wishlistItems.length > 0 && (
            <span className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-black leading-none text-white shadow-lg shadow-rose-900/30">
              {wishlistItems.length}
            </span>
          )}
        </Link>

        <button
          type="button"
          aria-label="Back to top"
          title="Back to top"
          onClick={handleScrollToTop}
          className="flex h-10 w-10 flex-col items-center justify-center text-[9px] font-semibold tracking-wide text-slate-900 transition hover:bg-slate-50 sm:h-12 sm:w-12 lg:h-14 lg:w-14 lg:text-[10px]"
        >
          <FiArrowUpIcon size={15} />
          <span>TOP</span>
        </button>
      </div>
      <SearchOverlay isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </aside>
  );
};

export default SideFloatingActionBar;
